// v1.11: 追讨信向导 — 4 步进度 (D+1 提醒 → D+7 警告 → D+14 催款 → D+30 律师函)
// 设计文档: docs/ANTI-FRAUD.md Part 6.1
//
// 流程:
//   1. 顶部 4 步进度条 (按 daysOverdue 高亮当前阶段)
//   2. 当前阶段模板 + 变量自动填 (从 order 数据)
//   3. 复制 / Gmail compose / 标记已发
//   4. 标记已发 → 写 compliance_event (后端已完成)
//
// Props:
//   - orderId, daysOverdue, currency, recommendedStage
//   - onToast(success/error) 父组件统一 toast
//   - onClose 关闭 modal
import { useState } from 'react';
import {
  type CollectionLang,
  type CollectionTemplate,
  type CollectionTemplateKey,
  collectionApi,
} from '../lib/api';
import { Icon } from './Icon';
import { Button } from './ui/Button';

const STAGE_ORDER: Array<{ key: CollectionTemplateKey; label: string; days: string }> = [
  { key: 'D1_REMINDER', label: 'D+1 友好提醒', days: '1 天' },
  { key: 'D7_WARNING', label: 'D+7 警告', days: '7 天' },
  { key: 'D14_URGENT', label: 'D+14 催款', days: '14 天' },
  { key: 'D30_FINAL', label: 'D+30 律师函', days: '30 天' },
];

interface CollectionWizardProps {
  orderId: string;
  daysOverdue: number;
  recommendedStage: CollectionTemplateKey;
  onToast: (kind: 'success' | 'error', msg: string) => void;
  onClose: () => void;
}

const LANG_META: Record<CollectionLang, { flag: string; label: string }> = {
  zh: { flag: '🇨🇳', label: '中文' },
  en: { flag: '🇺🇸', label: 'English' },
};

export function CollectionWizard({
  orderId,
  daysOverdue,
  recommendedStage,
  onToast,
  onClose,
}: CollectionWizardProps) {
  const [activeStage, setActiveStage] = useState<CollectionTemplateKey>(recommendedStage);
  const [activeLang, setActiveLang] = useState<CollectionLang>('en');
  const [rendered, setRendered] = useState<{
    subject: string;
    body: string;
    variables: Array<{ name: string; label: string; hint?: string }>;
    autoVars: Record<string, string>;
    missingVars: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 用户可手动补的变量 (不覆盖 auto)
  const [manualVars, setManualVars] = useState<Record<string, string>>({});

  async function handleRender() {
    setLoading(true);
    setError(null);
    try {
      const data = await collectionApi.generate({
        orderId,
        key: activeStage,
        lang: activeLang,
        variables: manualVars,
      });
      setRendered(data);
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg);
      onToast('error', `生成失败: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!rendered) {
      onToast('error', '请先生成追讨信');
      return;
    }
    const text = rendered.body;
    try {
      await navigator.clipboard.writeText(`主题: ${rendered.subject}\n\n${text}`);
      onToast('success', '已复制 (含主题)');
    } catch (e) {
      onToast('error', `复制失败: ${(e as Error).message}`);
    }
  }

  function handleGmail() {
    if (!rendered) {
      onToast('error', '请先生成追讨信');
      return;
    }
    const subject = encodeURIComponent(rendered.subject);
    const body = encodeURIComponent(rendered.body);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, '_blank');
  }

  function handleMarkSent() {
    // 后端在 generate 时已经写 audit log, 这里只是给用户一个"已标记"的反馈
    onToast('success', `已记录到 compliance_event: ${activeStage} (${activeLang})`);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-4 sm:pt-12 px-3 pb-6 overflow-y-auto">
      <div className="bg-surface border border-line rounded-md w-full max-w-2xl shadow-lg animate-fade-up">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div className="flex items-center gap-2">
            <Icon name="siren" size="md" className="text-bad" />
            <h2 className="text-base font-semibold">启动追讨</h2>
            <span className="text-xs text-ink-3">订单 #{orderId.slice(0, 8)}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-ink-3 hover:text-ink"
            aria-label="关闭"
          >
            <Icon name="x" size="md" />
          </button>
        </div>

        {/* 逾期提示 + 当前阶段 */}
        <div className="p-4 bg-bad/5 border-b border-line">
          <div className="flex items-center gap-2 text-sm">
            <Icon name="alert-triangle" size="sm" className="text-bad" />
            <span className="font-medium">客户已拖欠 {daysOverdue} 天</span>
            <span className="text-xs text-ink-3">
              (系统推荐: <b>{STAGE_ORDER.find((s) => s.key === recommendedStage)?.label}</b>)
            </span>
          </div>
        </div>

        {/* 4 步进度条 */}
        <div className="p-4 border-b border-line">
          <div className="grid grid-cols-4 gap-1">
            {STAGE_ORDER.map((s) => {
              const isActive = s.key === activeStage;
              const isCurrent = s.key === recommendedStage;
              return (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => {
                    setActiveStage(s.key);
                    setRendered(null);
                    setError(null);
                  }}
                  className={`p-2 text-xs rounded border transition-colors text-left ${
                    isActive
                      ? 'bg-accent text-white border-accent'
                      : isCurrent
                        ? 'border-bad/40 bg-bad/5 text-bad hover:bg-bad/10'
                        : 'border-line text-ink-2 hover:bg-surface-2'
                  }`}
                >
                  <div className="font-semibold">{s.label}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">超期 {s.days}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 语言切换 */}
        <div className="p-4 border-b border-line flex items-center gap-2">
          <span className="text-xs text-ink-3">语言:</span>
          {(Object.keys(LANG_META) as CollectionLang[]).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                setActiveLang(lang);
                setRendered(null);
              }}
              className={`px-2 py-1 text-xs rounded border ${
                activeLang === lang
                  ? 'bg-accent text-white border-accent'
                  : 'border-line hover:bg-surface-2'
              }`}
            >
              {LANG_META[lang].flag} {LANG_META[lang].label}
            </button>
          ))}
        </div>

        {/* 变量填写 (展示但不强制, 自动填的变量用户可改) */}
        {rendered && rendered.variables.length > 0 && (
          <div className="p-4 border-b border-line bg-surface-2">
            <div className="text-xs text-ink-3 mb-2">变量 (自动从订单数据填, 可手动覆盖):</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {rendered.variables.map((v) => {
                const autoVal = rendered.autoVars[v.name] ?? '';
                const cur = manualVars[v.name] ?? autoVal;
                return (
                  <label key={v.name} className="text-xs">
                    <span className="text-ink-2 flex items-center gap-1">
                      <span>{v.label}</span>
                      <code className="text-[10px] bg-surface px-1 rounded text-ink-3">
                        {`${'${'}${v.name}${'}'}`}
                      </code>
                      {rendered.missingVars.includes(v.name) && (
                        <span className="text-[10px] text-amber-600">未填</span>
                      )}
                    </span>
                    <input
                      type="text"
                      value={cur}
                      onChange={(e) => {
                        setManualVars((s) => ({ ...s, [v.name]: e.target.value }));
                        setRendered(null);
                      }}
                      maxLength={300}
                      placeholder={v.hint ?? autoVal}
                      className="mt-1 w-full px-2 py-1 border border-line rounded text-xs bg-surface focus:border-accent focus:outline-none"
                    />
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* 错误 */}
        {error && (
          <div className="p-4 bg-bad/5 border-b border-line text-xs text-bad">
            <Icon name="alert-circle" size="xs" className="inline mr-1" /> {error}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="p-4 border-b border-line flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            iconLeft="sparkles"
            loading={loading}
            onClick={handleRender}
          >
            生成追讨信
          </Button>
          {rendered && (
            <>
              <Button variant="secondary" size="sm" iconLeft="copy" onClick={handleCopy}>
                复制
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft="mail"
                onClick={handleGmail}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Gmail
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft="check"
                onClick={handleMarkSent}
                className="border-good/30 text-good hover:bg-good/5"
              >
                标记已发
              </Button>
            </>
          )}
        </div>

        {/* 渲染结果预览 */}
        {rendered && (
          <div className="p-4 max-h-72 overflow-y-auto">
            <div className="text-xs text-ink-3 mb-2">
              <b>主题:</b> {rendered.subject}
            </div>
            {rendered.missingVars.length > 0 && (
              <div className="text-[10px] text-amber-600 mb-2 flex items-center gap-1">
                <Icon name="alert-triangle" size="xs" />
                未填变量 ({rendered.missingVars.length}): {rendered.missingVars.join(', ')}
              </div>
            )}
            <pre className="text-xs text-ink-2 whitespace-pre-wrap font-sans leading-relaxed bg-surface-2 p-3 rounded">
              {rendered.body}
            </pre>
          </div>
        )}

        {/* 底部说明 */}
        <div className="p-3 bg-surface-2 border-t border-line text-[11px] text-ink-3 leading-relaxed">
          💡 标记已发会写入 compliance_event audit log, 后续可在订单页 / dashboard 查"已发过几次 D+7
          警告"。 模板来自 docs/ANTI-FRAUD.md Part 6.1 应急 SOP。
        </div>
      </div>
    </div>
  );
}
